import { io, Socket } from 'socket.io-client';
import type {
  WsConnectionAuth,
  WsMessagePayload,
  WsMessageUpdatedPayload,
  WsTypingPayload,
  WsTicketPayload,
  WsSubscribedPayload,
  RaRealtimeEvent,
  WsReactionPayload,
  WsParticipantPayload,
  WsReadReceiptPayload,
  WsChannelSubscribedPayload,
  WsChannelStatusChangedPayload,
  WsChannelQRUpdatedPayload,
  WsContactUpdatedPayload,
  MessageStatusEvent,
} from '../types/chat.types';
import {
  ChatEvent,
  TicketEvent,
  ChannelEvent,
  ContactEvent,
  ConnectionEvent,
  RA_REALTIME_EVENT,
} from '../types/websocket-events';

import { API_URL } from '../httpClient';

// Event callback types
type MessageCallback = (payload: WsMessagePayload) => void;
type MessageUpdatedCallback = (payload: WsMessageUpdatedPayload) => void;
type TypingCallback = (payload: WsTypingPayload) => void;
type TicketCallback = (payload: WsTicketPayload) => void;
type RaRealtimeCallback = (payload: RaRealtimeEvent) => void;
type SubscribedCallback = (payload: WsSubscribedPayload) => void;
type ReactionCallback = (payload: WsReactionPayload) => void;
type ParticipantCallback = (payload: WsParticipantPayload) => void;
type ReadReceiptCallback = (payload: WsReadReceiptPayload) => void;
type ConnectionCallback = () => void;
type ErrorCallback = (error: Error) => void;

// Contact event callback types
type ContactUpdatedCallback = (payload: WsContactUpdatedPayload) => void;

// Channel event callback types
type ChannelSubscribedCallback = (payload: WsChannelSubscribedPayload) => void;
type ChannelStatusChangedCallback = (payload: WsChannelStatusChangedPayload) => void;
type ChannelQRUpdatedCallback = (payload: WsChannelQRUpdatedPayload) => void;

interface EventListeners {
  // Chat events
  [ChatEvent.NEW_MESSAGE]: MessageCallback[];
  [ChatEvent.MESSAGE_UPDATED]: MessageUpdatedCallback[];
  [ChatEvent.MESSAGE_DELETED]: ((payload: { conversationId: number; messageId: number }) => void)[];
  [ChatEvent.MESSAGE_STATUS]: ((payload: MessageStatusEvent) => void)[];
  [ChatEvent.REACTION_ADDED]: ReactionCallback[];
  [ChatEvent.REACTION_REMOVED]: ReactionCallback[];
  [ChatEvent.PARTICIPANT_ADDED]: ParticipantCallback[];
  [ChatEvent.PARTICIPANT_REMOVED]: ParticipantCallback[];
  [ChatEvent.READ_RECEIPT]: ReadReceiptCallback[];
  [ChatEvent.USER_TYPING]: TypingCallback[];
  [ChatEvent.USER_STOPPED_TYPING]: TypingCallback[];
  [ChatEvent.JOINED]: ((payload: { userId: number; conversationCount: number }) => void)[];
  [ChatEvent.SUBSCRIBED]: SubscribedCallback[];
  [ChatEvent.CONVERSATION_CREATED]: ((payload: { conversation: unknown }) => void)[];

  // Ticket events
  [TicketEvent.CREATED]: ((payload: { ticket: unknown }) => void)[];
  [TicketEvent.UPDATED]: TicketCallback[];
  [TicketEvent.STATUS_CHANGED]: TicketCallback[];
  [TicketEvent.ASSIGNED]: TicketCallback[];
  [TicketEvent.ASSIGNED_TO_YOU]: TicketCallback[];
  [TicketEvent.UNASSIGNED]: TicketCallback[];
  [TicketEvent.PRIORITY_CHANGED]: TicketCallback[];
  [TicketEvent.QUEUE_CHANGED]: TicketCallback[];
  [TicketEvent.NOTE_ADDED]: ((payload: { ticketUuid: string; note: unknown }) => void)[];
  [TicketEvent.SLA_WARNING]: ((payload: { ticketUuid: string; slaType: string; breachAt: string; timeRemaining: number }) => void)[];
  [TicketEvent.SLA_BREACHED]: ((payload: { ticketUuid: string; slaType: string; breachedAt: string }) => void)[];
  [TicketEvent.DELETED]: ((payload: { ticketUuid: string }) => void)[];
  [TicketEvent.SUBSCRIBED]: SubscribedCallback[];
  [TicketEvent.ALL_SUBSCRIBED]: ((payload: { tenantUuid: string }) => void)[];

  // React Admin realtime event
  [RA_REALTIME_EVENT]: RaRealtimeCallback[];

  // Contact events
  [ContactEvent.UPDATED]: ContactUpdatedCallback[];

  // Channel events
  [ChannelEvent.SUBSCRIBED]: ChannelSubscribedCallback[];
  [ChannelEvent.UNSUBSCRIBED]: ChannelSubscribedCallback[];
  [ChannelEvent.STATUS_CHANGED]: ChannelStatusChangedCallback[];
  [ChannelEvent.QR_UPDATED]: ChannelQRUpdatedCallback[];

  // Connection events
  [ConnectionEvent.CONNECT]: ConnectionCallback[];
  [ConnectionEvent.DISCONNECT]: ConnectionCallback[];
  [ConnectionEvent.ERROR]: ErrorCallback[];
}

type EventName = keyof EventListeners;

/**
 * WebSocketClient - Unified WebSocket client for all real-time events.
 *
 * Uses a single /realtime namespace for all events (chat, ticket, notification, channel).
 * This simplifies connection management, reconnection logic, and state management.
 *
 * Event Prefixes:
 * - chat:*          Chat/messaging events
 * - ticket:*        Ticket management events
 * - channel:*       Channel connection events
 * - ra-realtime     React Admin realtime events
 */
class WebSocketClient {
  private socket: Socket | null = null;
  private auth: WsConnectionAuth | null = null;
  /** Conversation IDs that should be re-subscribed on reconnect */
  private activeConversationSubscriptions = new Set<number>();
  /** Ticket IDs that should be re-subscribed on reconnect */
  private activeTicketSubscriptions = new Set<number>();
  /** Channel connection IDs that should be re-subscribed on reconnect */
  private activeChannelSubscriptions = new Set<number>();
  private listeners: EventListeners = {
    [ChatEvent.NEW_MESSAGE]: [],
    [ChatEvent.MESSAGE_UPDATED]: [],
    [ChatEvent.MESSAGE_DELETED]: [],
    [ChatEvent.MESSAGE_STATUS]: [],
    [ChatEvent.REACTION_ADDED]: [],
    [ChatEvent.REACTION_REMOVED]: [],
    [ChatEvent.PARTICIPANT_ADDED]: [],
    [ChatEvent.PARTICIPANT_REMOVED]: [],
    [ChatEvent.READ_RECEIPT]: [],
    [ChatEvent.USER_TYPING]: [],
    [ChatEvent.USER_STOPPED_TYPING]: [],
    [ChatEvent.JOINED]: [],
    [ChatEvent.SUBSCRIBED]: [],
    [ChatEvent.CONVERSATION_CREATED]: [],
    [TicketEvent.CREATED]: [],
    [TicketEvent.UPDATED]: [],
    [TicketEvent.STATUS_CHANGED]: [],
    [TicketEvent.ASSIGNED]: [],
    [TicketEvent.ASSIGNED_TO_YOU]: [],
    [TicketEvent.UNASSIGNED]: [],
    [TicketEvent.PRIORITY_CHANGED]: [],
    [TicketEvent.QUEUE_CHANGED]: [],
    [TicketEvent.NOTE_ADDED]: [],
    [TicketEvent.SLA_WARNING]: [],
    [TicketEvent.SLA_BREACHED]: [],
    [TicketEvent.DELETED]: [],
    [TicketEvent.SUBSCRIBED]: [],
    [TicketEvent.ALL_SUBSCRIBED]: [],
    [RA_REALTIME_EVENT]: [],
    [ContactEvent.UPDATED]: [],
    [ChannelEvent.SUBSCRIBED]: [],
    [ChannelEvent.UNSUBSCRIBED]: [],
    [ChannelEvent.STATUS_CHANGED]: [],
    [ChannelEvent.QR_UPDATED]: [],
    [ConnectionEvent.CONNECT]: [],
    [ConnectionEvent.DISCONNECT]: [],
    [ConnectionEvent.ERROR]: [],
  };
  private reconnectAttempts = 0;
  private reconnectDelay = 1000;
  private connectionEpoch = 0;
  private visibilityHandler: (() => void) | null = null;

  /**
   * Connect to WebSocket server using unified /realtime namespace
   */
  connect(auth?: WsConnectionAuth): void {
    // Use provided auth or get from localStorage
    this.auth = auth || {
      tenantUuid: localStorage.getItem('tenantUuid') || '',
      token: localStorage.getItem('token') || '',
    };

    if (!this.auth.tenantUuid || !this.auth.token) {
      console.warn('[WebSocket] Missing auth credentials, skipping connection');
      return;
    }

    if (this.socket?.connected) return;

    // Clean up any existing socket before creating a new one
    if (this.socket) {
      this.socket.removeAllListeners();
      this.socket.disconnect();
      this.socket = null;
    }

    this.socket = io(`${API_URL}/realtime`, {
      auth: this.auth,
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionAttempts: Infinity, // Infinite reconnection attempts
      reconnectionDelay: this.reconnectDelay,
      reconnectionDelayMax: 10000, // Max 10 seconds between attempts
    });

    this.setupListeners();
  }

  private setupListeners(): void {
    if (!this.socket) return;

    // Connection events
    this.socket.on(ConnectionEvent.CONNECT, () => {
      console.log('[WebSocket] Connected:', this.socket?.id);
      this.reconnectAttempts = 0;
      this.connectionEpoch++;
      // Auto-join chat on connection
      this.socket?.emit(ChatEvent.JOIN);
      // Re-subscribe to all active subscriptions after reconnect
      this.resubscribeAll();
      this.emit(ConnectionEvent.CONNECT);
    });

    this.socket.on(ConnectionEvent.DISCONNECT, () => {
      console.log('[WebSocket] Disconnected');
      this.emit(ConnectionEvent.DISCONNECT);
    });

    this.socket.on(ConnectionEvent.CONNECT_ERROR, (error: Error) => {
      console.error('[WebSocket] Connection error:', error.message);
      this.reconnectAttempts++;
      this.emit(ConnectionEvent.ERROR, error);
    });

    // Chat events
    this.socket.on(ChatEvent.JOINED, (payload) => this.emit(ChatEvent.JOINED, payload));
    this.socket.on(ChatEvent.SUBSCRIBED, (payload) => this.emit(ChatEvent.SUBSCRIBED, payload));
    this.socket.on(ChatEvent.NEW_MESSAGE, (payload) => this.emit(ChatEvent.NEW_MESSAGE, payload));
    this.socket.on(ChatEvent.MESSAGE_UPDATED, (payload) => this.emit(ChatEvent.MESSAGE_UPDATED, payload));
    this.socket.on(ChatEvent.MESSAGE_DELETED, (payload) => this.emit(ChatEvent.MESSAGE_DELETED, payload));
    this.socket.on(ChatEvent.MESSAGE_STATUS, (payload) => this.emit(ChatEvent.MESSAGE_STATUS, payload));
    this.socket.on(ChatEvent.REACTION_ADDED, (payload) => this.emit(ChatEvent.REACTION_ADDED, payload));
    this.socket.on(ChatEvent.REACTION_REMOVED, (payload) => this.emit(ChatEvent.REACTION_REMOVED, payload));
    this.socket.on(ChatEvent.PARTICIPANT_ADDED, (payload) => this.emit(ChatEvent.PARTICIPANT_ADDED, payload));
    this.socket.on(ChatEvent.PARTICIPANT_REMOVED, (payload) => this.emit(ChatEvent.PARTICIPANT_REMOVED, payload));
    this.socket.on(ChatEvent.READ_RECEIPT, (payload) => this.emit(ChatEvent.READ_RECEIPT, payload));
    this.socket.on(ChatEvent.USER_TYPING, (payload) => this.emit(ChatEvent.USER_TYPING, payload));
    this.socket.on(ChatEvent.USER_STOPPED_TYPING, (payload) => this.emit(ChatEvent.USER_STOPPED_TYPING, payload));
    this.socket.on(ChatEvent.CONVERSATION_CREATED, (payload) => this.emit(ChatEvent.CONVERSATION_CREATED, payload));

    // Ticket events
    this.socket.on(TicketEvent.SUBSCRIBED, (payload) => this.emit(TicketEvent.SUBSCRIBED, payload));
    this.socket.on(TicketEvent.ALL_SUBSCRIBED, (payload) => this.emit(TicketEvent.ALL_SUBSCRIBED, payload));
    this.socket.on(TicketEvent.CREATED, (payload) => this.emit(TicketEvent.CREATED, payload));
    this.socket.on(TicketEvent.UPDATED, (payload) => this.emit(TicketEvent.UPDATED, payload));
    this.socket.on(TicketEvent.STATUS_CHANGED, (payload) => this.emit(TicketEvent.STATUS_CHANGED, payload));
    this.socket.on(TicketEvent.ASSIGNED, (payload) => this.emit(TicketEvent.ASSIGNED, payload));
    this.socket.on(TicketEvent.ASSIGNED_TO_YOU, (payload) => this.emit(TicketEvent.ASSIGNED_TO_YOU, payload));
    this.socket.on(TicketEvent.UNASSIGNED, (payload) => this.emit(TicketEvent.UNASSIGNED, payload));
    this.socket.on(TicketEvent.PRIORITY_CHANGED, (payload) => this.emit(TicketEvent.PRIORITY_CHANGED, payload));
    this.socket.on(TicketEvent.QUEUE_CHANGED, (payload) => this.emit(TicketEvent.QUEUE_CHANGED, payload));
    this.socket.on(TicketEvent.NOTE_ADDED, (payload) => this.emit(TicketEvent.NOTE_ADDED, payload));
    this.socket.on(TicketEvent.SLA_WARNING, (payload) => this.emit(TicketEvent.SLA_WARNING, payload));
    this.socket.on(TicketEvent.SLA_BREACHED, (payload) => this.emit(TicketEvent.SLA_BREACHED, payload));
    this.socket.on(TicketEvent.DELETED, (payload) => this.emit(TicketEvent.DELETED, payload));

    // Contact events
    this.socket.on(ContactEvent.UPDATED, (payload) => this.emit(ContactEvent.UPDATED, payload));

    // Channel events
    this.socket.on(ChannelEvent.SUBSCRIBED, (payload) => this.emit(ChannelEvent.SUBSCRIBED, payload));
    this.socket.on(ChannelEvent.UNSUBSCRIBED, (payload) => this.emit(ChannelEvent.UNSUBSCRIBED, payload));
    this.socket.on(ChannelEvent.STATUS_CHANGED, (payload) => this.emit(ChannelEvent.STATUS_CHANGED, payload));
    this.socket.on(ChannelEvent.QR_UPDATED, (payload) => this.emit(ChannelEvent.QR_UPDATED, payload));

    // React Admin realtime event
    this.socket.on(RA_REALTIME_EVENT, (payload) => this.emit(RA_REALTIME_EVENT, payload));

    // Visibility change detection: force reconnect when tab becomes visible while disconnected
    if (this.visibilityHandler) {
      document.removeEventListener('visibilitychange', this.visibilityHandler);
    }
    this.visibilityHandler = () => {
      if (document.visibilityState === 'visible' && this.socket) {
        if (!this.socket.connected) {
          console.log('[WebSocket] Tab became visible while disconnected, forcing reconnect');
          this.socket.disconnect();
          this.socket.connect();
        } else {
          // Connection looks alive but may be stale after long idle.
          // Re-subscribe to all active subscriptions to guarantee the
          // server-side rooms are still intact (server may have evicted
          // the client from rooms during inactivity).
          console.log('[WebSocket] Tab became visible, re-subscribing to ensure rooms are intact');
          this.resubscribeAll();
        }
      }
    };
    document.addEventListener('visibilitychange', this.visibilityHandler);
  }

  /**
   * Disconnect from WebSocket server
   */
  disconnect(): void {
    if (this.visibilityHandler) {
      document.removeEventListener('visibilitychange', this.visibilityHandler);
      this.visibilityHandler = null;
    }
    if (this.socket) {
      this.socket.removeAllListeners();
      this.socket.disconnect();
      this.socket = null;
    }
    this.auth = null;
    this.activeConversationSubscriptions.clear();
    this.activeTicketSubscriptions.clear();
    this.activeChannelSubscriptions.clear();
  }

  /**
   * Check if connected
   */
  isConnected(): boolean {
    return this.socket?.connected || false;
  }

  /**
   * Get the current number of reconnection attempts
   */
  getReconnectAttempts(): number {
    return this.reconnectAttempts;
  }

  /**
   * Get the current connection epoch (increments on each successful connection).
   * Useful for consumers to detect reconnections.
   */
  getConnectionEpoch(): number {
    return this.connectionEpoch;
  }

  // ─────────────────────────────────────────────────────────────────────────
  // CHAT ACTIONS
  // ─────────────────────────────────────────────────────────────────────────

  /**
   * Subscribe to a specific conversation.
   * The subscription is tracked so it will be automatically
   * re-established after a reconnection.
   */
  subscribeToConversation(conversationId: number): void {
    this.activeConversationSubscriptions.add(conversationId);
    if (!this.socket?.connected) {
      console.warn('[WebSocket] Cannot subscribe yet - not connected, will subscribe on reconnect');
      return;
    }
    this.socket.emit(ChatEvent.SUBSCRIBE_CONVERSATION, { conversationId });
  }

  /**
   * Unsubscribe from a conversation
   */
  unsubscribeFromConversation(conversationId: number): void {
    this.activeConversationSubscriptions.delete(conversationId);
    if (!this.socket?.connected) return;
    this.socket.emit(ChatEvent.UNSUBSCRIBE_CONVERSATION, { conversationId });
  }

  /**
   * Start typing indicator
   */
  startTyping(conversationId: number): void {
    if (!this.socket?.connected) return;
    this.socket.emit(ChatEvent.TYPING_START, { conversationId });
  }

  /**
   * Stop typing indicator
   */
  stopTyping(conversationId: number): void {
    if (!this.socket?.connected) return;
    this.socket.emit(ChatEvent.TYPING_STOP, { conversationId });
  }

  /**
   * Mark a conversation as read up to a specific message
   */
  markConversationRead(conversationId: number, lastReadMessageId: number): void {
    if (!this.socket?.connected) return;
    this.socket.emit(ChatEvent.MARK_READ, { conversationId, lastReadMessageId });
  }

  // ─────────────────────────────────────────────────────────────────────────
  // TICKET ACTIONS
  // ─────────────────────────────────────────────────────────────────────────

  /**
   * Subscribe to a specific ticket.
   * The subscription is tracked so it will be automatically
   * re-established after a reconnection.
   */
  subscribeToTicket(ticketId: number): void {
    this.activeTicketSubscriptions.add(ticketId);
    if (!this.socket?.connected) {
      console.warn('[WebSocket] Cannot subscribe to ticket yet - not connected, will subscribe on reconnect');
      return;
    }
    this.socket.emit(TicketEvent.SUBSCRIBE, { ticketId });
  }

  /**
   * Unsubscribe from a ticket
   */
  unsubscribeFromTicket(ticketId: number): void {
    this.activeTicketSubscriptions.delete(ticketId);
    if (!this.socket?.connected) return;
    this.socket.emit(TicketEvent.UNSUBSCRIBE, { ticketId });
  }

  /**
   * Subscribe to a ticket queue
   */
  subscribeToQueue(queueId: number): void {
    if (!this.socket?.connected) return;
    this.socket.emit(TicketEvent.SUBSCRIBE_QUEUE, { queueId });
  }

  /**
   * Subscribe to all tickets (requires permission)
   */
  subscribeToAllTickets(): void {
    if (!this.socket) {
      console.warn('[WebSocket] Cannot subscribe to all tickets - socket not initialized');
      return;
    }

    if (!this.socket.connected) {
      // If not connected yet, wait for connection then subscribe
      this.socket.once(ConnectionEvent.CONNECT, () => {
        this.socket!.emit(TicketEvent.SUBSCRIBE_ALL);
      });
      return;
    }

    // Already connected, subscribe immediately
    this.socket.emit(TicketEvent.SUBSCRIBE_ALL);
  }

  // ─────────────────────────────────────────────────────────────────────────
  // CHANNEL ACTIONS
  // ─────────────────────────────────────────────────────────────────────────

  /**
   * Check if channel socket is connected (same as isConnected now)
   */
  isChannelConnected(): boolean {
    return this.socket?.connected || false;
  }

  /**
   * Subscribe to a channel connection for real-time status updates.
   * The subscription is tracked so it will be automatically
   * re-established after a reconnection.
   * @param channelConnectionId - ID of the channel connection
   */
  subscribeToChannelConnection(channelConnectionId: number): void {
    this.activeChannelSubscriptions.add(channelConnectionId);
    if (!this.socket?.connected) {
      console.warn('[WebSocket] Cannot subscribe to channel yet - not connected, will subscribe on reconnect');
      return;
    }
    this.socket.emit(ChannelEvent.SUBSCRIBE, { channelConnectionId });
  }

  /**
   * Unsubscribe from a channel connection.
   * @param channelConnectionId - ID of the channel connection
   */
  unsubscribeFromChannelConnection(channelConnectionId: number): void {
    this.activeChannelSubscriptions.delete(channelConnectionId);
    if (!this.socket?.connected) return;
    this.socket.emit(ChannelEvent.UNSUBSCRIBE, { channelConnectionId });
  }

  // ─────────────────────────────────────────────────────────────────────────
  // RECONNECTION
  // ─────────────────────────────────────────────────────────────────────────

  /**
   * Re-subscribe to all tracked subscriptions.
   * Called automatically after a successful reconnection.
   */
  private resubscribeAll(): void {
    if (!this.socket?.connected) return;

    for (const conversationId of this.activeConversationSubscriptions) {
      console.log('[WebSocket] Re-subscribing to conversation', conversationId);
      this.socket.emit(ChatEvent.SUBSCRIBE_CONVERSATION, { conversationId });
    }
    for (const ticketId of this.activeTicketSubscriptions) {
      console.log('[WebSocket] Re-subscribing to ticket', ticketId);
      this.socket.emit(TicketEvent.SUBSCRIBE, { ticketId });
    }
    for (const channelConnectionId of this.activeChannelSubscriptions) {
      console.log('[WebSocket] Re-subscribing to channel', channelConnectionId);
      this.socket.emit(ChannelEvent.SUBSCRIBE, { channelConnectionId });
    }
  }

  // ─────────────────────────────────────────────────────────────────────────
  // EVENT HANDLING
  // ─────────────────────────────────────────────────────────────────────────

  /**
   * Add event listener
   */
  on<E extends EventName>(event: E, callback: EventListeners[E][number]): void {
    (this.listeners[event] as unknown[]).push(callback);
  }

  /**
   * Remove event listener
   */
  off<E extends EventName>(event: E, callback: EventListeners[E][number]): void {
    const listeners = this.listeners[event] as unknown[];
    const index = listeners.indexOf(callback);
    if (index > -1) {
      listeners.splice(index, 1);
    }
  }

  /**
   * Emit event to all listeners
   */
  private emit<E extends EventName>(event: E, payload?: Parameters<EventListeners[E][number]>[0]): void {
    const listeners = this.listeners[event] as ((payload?: unknown) => void)[];
    listeners.forEach((callback) => callback(payload));
  }

  /**
   * Remove all listeners for an event
   */
  removeAllListeners(event?: EventName): void {
    if (event) {
      this.listeners[event] = [] as unknown as EventListeners[typeof event];
    } else {
      Object.keys(this.listeners).forEach((key) => {
        this.listeners[key as EventName] = [] as unknown as EventListeners[EventName];
      });
    }
  }
}

// Export singleton instance
export const wsClient = new WebSocketClient();

// Export class for testing
export { WebSocketClient };
